/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { useState } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { UI_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { ExternalLinkModal } from '../../../components/externalLinkModal';
import type { EgressFilteringService } from '@forge/egress';

import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

import type { Extension } from '../../../web-client';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../../../analytics';

import { type NavigatePayload, BridgeClientError } from '..';

import { navigate } from './navigate';
import { getOrigin } from './getOrigin';
import { useEgressPermissionService } from '../../../utils/useEgressPermissionService';
import { makeUrl } from './makeUrl';
import { TRUSTED_ROOT_DOMAINS } from '../constants/trustedDomains';
import { parseExtensionId } from '../../../utils/parseExtensionId';

type ModalState =
	| {
			onCancel: () => void;
			onConfirm: () => void;
			state: 'open';
			url: URL;
	  }
	| {
			state: 'closed';
	  };

export function resolveNavigationAppId({
	appId,
	extension,
}: {
	appId?: string;
	extension: Partial<Pick<Extension, 'appId' | 'id'>>;
}): string | undefined {
	if (appId) {
		return appId;
	}
	if (extension.appId) {
		return extension.appId;
	}
	if (extension.id) {
		try {
			return parseExtensionId(extension.id).appId;
		} catch {
			return undefined;
		}
	}
	return undefined;
}

export function useNavigation({
	extension,
	push,
	appId,
	onNavigate,
	openNewTabAsPopupWindow,
}: {
	appId?: string;
	extension: Pick<Extension, 'properties' | 'egress'> & Partial<Pick<Extension, 'appId' | 'id'>>;
	onNavigate?: (payload: NavigatePayload) => void;
	openNewTabAsPopupWindow?: boolean;
	push?: (path: string, state?: any) => void;
}) {
	const [modalState, setModalState] = useState<ModalState>({ state: 'closed' });
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const egressService = useEgressPermissionService('FETCH_CLIENT_SIDE', extension.egress);
	const resolvedAppId = resolveNavigationAppId({ appId, extension });

	return {
		onNavigate: async ({ type, url: urlString }: NavigatePayload): Promise<void> => {
			const url = makeUrl(urlString);
			const linkType = getLinkType(url, egressService);
			if (linkType === 'invalid' || !url) {
				throw new BridgeClientError(`link is invalid: ${urlString}`);
			}
			if (linkType === 'trusted') {
				createAnalyticsEvent({
					eventType: UI_EVENT_TYPE,
					data: {
						action: 'clicked',
						actionSubject: 'trustedLink',
						attributes: getTrustedLinkAnalyticsAttributes(url),
					},
				}).fire(FORGE_UI_ANALYTICS_CHANNEL);
				onNavigate?.({ type, url: url.href });
				navigate(url.href, type, push, resolvedAppId, openNewTabAsPopupWindow);
				return;
			}
			const analyticsAttributes = getAnalyticsAttributes(url);
			return new Promise((resolve, reject) => {
				// TODO: remove this event once we have switched over to the new event schema
				// https://ecosystem-platform.atlassian.net/browse/EXT-2196
				createAnalyticsEvent({
					eventType: UI_EVENT_TYPE,
					data: {
						action: 'viewed',
						actionSubject: 'externalLinkModal',
						attributes: analyticsAttributes,
					},
				}).fire(FORGE_UI_ANALYTICS_CHANNEL);
				setModalState({
					state: 'open',
					url,
					onConfirm: () => {
						onNavigate?.({ type, url: url.href });
						navigate(url.href, type, push, resolvedAppId, openNewTabAsPopupWindow);
						// TODO: remove this event once we have switched over to the new event schema
						// https://ecosystem-platform.atlassian.net/browse/EXT-2196
						createAnalyticsEvent({
							eventType: UI_EVENT_TYPE,
							data: {
								action: 'approved',
								actionSubject: 'externalLinkModal',
								attributes: analyticsAttributes,
							},
						}).fire(FORGE_UI_ANALYTICS_CHANNEL);
						resolve();
					},
					onCancel: () => {
						// TODO: remove this event once we have switched over to the new event schema
						// https://ecosystem-platform.atlassian.net/browse/EXT-2196
						createAnalyticsEvent({
							eventType: UI_EVENT_TYPE,
							data: {
								action: 'cancelled',
								actionSubject: 'externalLinkModal',
								attributes: analyticsAttributes,
							},
						}).fire(FORGE_UI_ANALYTICS_CHANNEL);
						reject(new BridgeClientError('cancelled'));
					},
				});
			});
		},

		getModalJsx: (): JSX.Element => {
			if (modalState.state === 'closed') {
				return <ModalTransition />;
			}
			return (
				<ExternalLinkModal
					url={modalState.url}
					extensionTitle={extension.properties?.title}
					onCancel={() => {
						setModalState({ state: 'closed' });
						modalState.onCancel();
					}}
					onConfirm={() => {
						modalState.onConfirm();
						setModalState({ state: 'closed' });
					}}
				/>
			);
		},
	};
}

const getRootDomain = (url: URL) => {
	const hostnameParts = url.hostname.split('.');
	// Root domain consists of the last two parts of the hostname
	if (hostnameParts.length > 1) {
		return hostnameParts.slice(-2).join('.');
	} else {
		// If there's only one part, return it (e.g., 'localhost')
		return url.hostname;
	}
};

// Exported for testing only
export function getLinkType(
	url: URL | undefined,
	egressService: EgressFilteringService,
): 'external' | 'trusted' | 'invalid' {
	if (!url) {
		return 'invalid';
	}

	if (url.protocol === 'mailto:') {
		return 'trusted';
	}
	if (url.protocol === 'tel:') {
		return 'trusted';
	}

	try {
		// URL is listed under permissions in manifest
		if (egressService.isValidUrl(url.href)) {
			return 'trusted';
		}

		// URL is from same origin
		if (url.origin === getOrigin()) {
			return 'trusted';
		}

		// URL is under a trusted atlassian domain
		const rootDomain = getRootDomain(url);
		if (TRUSTED_ROOT_DOMAINS.includes(rootDomain)) {
			return 'trusted';
		}

		if (['https:', 'http:'].includes(url.protocol)) {
			return 'external';
		}
	} catch {}
	return 'invalid';
}

const getQueryStringSize = (url: URL): number =>
	url.search.length > 1 ? url.search.length - 1 : 0;

// Exported for testing only
export function getAnalyticsAttributes(url: URL): Record<string, any> {
	// Don't count the initial `?` in the query string length.
	return {
		url: url.href,
		queryStringSize: getQueryStringSize(url),
	};
}

export function getTrustedLinkAnalyticsAttributes(url: URL): Record<string, any> {
	return {
		queryStringSize: getQueryStringSize(url),
	};
}
